import React, {FC, ReactElement, useEffect, useRef, useState} from 'react';
import {Chip, ChipProps, Box, Typography} from '@mui/material';
import AurIcon, {IconNames, IconColors} from '../aur-icon/AurIcon';
import AurIconButton from '../aur-button/AurIconButton';
import {t} from 'i18next';

export interface FilterSelection {
  id: number;
  value: string;
}

export type AurChipProps = ChipProps & {
  icon?: ReactElement;
  filterName?: string;
  filterSelection?: FilterSelection[];
  orStatement?: string;
  showRemove?: boolean;
  onRemove?: () => void;
};

const AurChip: FC<AurChipProps> = (props) => {
  const {
    icon,
    filterName,
    filterSelection,
    orStatement = 'OR',
    showRemove = false,
    onRemove,
    ...chipProps
  } = props;
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 150;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const {scrollLeft, scrollWidth, clientWidth} = container;
      setShowLeftButton(scrollLeft > 0);
      setShowRightButton(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };

  const handleKeyDownForRemove = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (onRemove) onRemove();
    }
  };

  useEffect(() => {
    checkScroll();
  }, [filterSelection?.length]);
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      checkScroll();
      container.addEventListener('scroll', checkScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
    };
  }, []);

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      borderRadius="25px"
      padding="4px 8px 4px 0px"
      bgcolor="#21253D"
      color="#F2F4F7"
      gap={1}
      border="1px solid #5E3D90"
      height={32}
      overflow="hidden"
    >
      {(icon || filterName) && (
        <Chip
          label={
            <div className={`chip-filter-name-label`}>
              {icon}
              <Typography className="typo-text label3_size">
                {filterName !== 'Search' ? filterName : ''}
              </Typography>
            </div>
          }
          className={`chip-filter-name ${filterName ? '' : 'chip-filter-name-only-icon'}`}
          {...chipProps}
        />
      )}
      {showLeftButton && (
        <AurIconButton
          onClick={() => scroll('left')}
          ariaLabel={`${t('common.aurChip.scrollLeft')}`}
          icon={<AurIcon icon={IconNames.ChevronLeft} size="medium" />}
          className="scroll scroll-left"
          dataTestId="Scroll Left"
        />
      )}
      <Box
        ref={scrollContainerRef}
        display="flex"
        overflow="hidden"
        whiteSpace="nowrap"
        flex="1"
        data-testid="scroll-container"
        aria-label="scroll-container"
      >
        {filterSelection?.map((selection, index) => (
          <React.Fragment key={selection.id}>
            <Chip
              className={`chip-filter-selection ${!filterName && !icon && index === 0 ? 'extra-padding' : ''}`}
              label={
                <Typography className="typo-text body3_size">
                  {selection.value}
                </Typography>
              }
              size="small"
            />
            {index < filterSelection.length - 1 && (
              <Chip
                label={<Typography>{orStatement}</Typography>}
                className="chip-or-block"
                size="small"
              />
            )}
          </React.Fragment>
        ))}
      </Box>
      {showRightButton && (
        <AurIconButton
          onClick={() => scroll('right')}
          ariaLabel={`${t('common.aurChip.scrollRight')}`}
          icon={<AurIcon icon={IconNames.ChevronRight} size="medium" />}
          className="scroll"
          dataTestId="Scroll Right"
        />
      )}
      {showRemove && filterSelection && (
        <div
          role="button"
          aria-label={`${t('common.aurChip.clickToClear')} ${filterName} ${t('common.aurChip.filter')}`}
          className="chip-close-icon-block"
          onClick={onRemove}
          tabIndex={0}
          onKeyDown={handleKeyDownForRemove}
        >
          <AurIcon
            size="small"
            icon={IconNames.Close}
            color={IconColors.Custom}
            customClassName="chip-close-icon"
          />
        </div>
      )}
    </Box>
  );
};

export default AurChip;
